package com.evoapps.evoshift.ui.user

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxHeight
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Add
import androidx.compose.material.icons.filled.EventNote
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.PlainTooltip
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.material3.TooltipAnchorPosition
import androidx.compose.material3.TooltipBox
import androidx.compose.material3.TooltipDefaults
import androidx.compose.material3.rememberTooltipState
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.drawBehind
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextDecoration
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.widgets.IconButtonWithTooltip
import com.evoapps.evoshift.domain.model.AbsenceStatus
import com.evoapps.evoshift.domain.requests.RequestType
import com.evoapps.evoshift.domain.timeline.RequestLane
import com.evoapps.evoshift.domain.timeline.RequestSpanStyle
import com.evoapps.evoshift.domain.timeline.TimelineRequest
import com.evoapps.evoshift.domain.timeline.TimelineRequests
import com.evoapps.evoshift.ui.AppAppearance
import com.evoapps.evoshift.ui.DarkText
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.ThemePalette
import com.evoapps.evoshift.ui.util.label
import com.evoapps.evoshift.widgets.DialogAction
import com.evoapps.evoshift.widgets.EvoDialog
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.absences_action_approve
import evoshift.composeapp.generated.resources.absences_action_reject
import evoshift.composeapp.generated.resources.absences_rejection_reason
import evoshift.composeapp.generated.resources.timeline_add_to
import evoshift.composeapp.generated.resources.timeline_empty_lane
import evoshift.composeapp.generated.resources.timeline_from_until
import evoshift.composeapp.generated.resources.timeline_hide_rejected
import evoshift.composeapp.generated.resources.timeline_lane_absences
import evoshift.composeapp.generated.resources.timeline_lane_overtime
import evoshift.composeapp.generated.resources.timeline_lane_remote_events
import evoshift.composeapp.generated.resources.timeline_request_close
import evoshift.composeapp.generated.resources.timeline_request_description
import evoshift.composeapp.generated.resources.timeline_request_hours
import evoshift.composeapp.generated.resources.timeline_request_no_drag
import evoshift.composeapp.generated.resources.timeline_request_note
import evoshift.composeapp.generated.resources.timeline_request_place
import evoshift.composeapp.generated.resources.timeline_request_status
import evoshift.composeapp.generated.resources.timeline_request_tooltip
import evoshift.composeapp.generated.resources.timeline_request_withdraw
import evoshift.composeapp.generated.resources.timeline_requests_hint
import evoshift.composeapp.generated.resources.timeline_requests_title
import evoshift.composeapp.generated.resources.timeline_show_rejected
import kotlinx.datetime.LocalDate
import kotlinx.datetime.daysUntil
import org.jetbrains.compose.resources.stringResource
import kotlin.math.roundToInt

/** SH-224: test tags of the request lanes. */
object TimelineRequestTags {
    const val SECTION = "timeline-requests"
    const val TOGGLE = "timeline-requests-toggle"
    const val DETAIL = "timeline-request-detail"
    fun lane(lane: RequestLane) = "timeline-request-lane-${lane.name}"
    fun request(key: String) = "timeline-request-$key"
}

/** A request span's colours: its fill, its edge (and how thick) and its text. */
data class RequestSpanColors(val fill: Color, val border: Color, val borderWidth: Dp, val text: Color) {
    companion object {
        /** The card the lanes lie on. */
        val Track = Color.White

        /** An approved request's fill: a light tint of its lane (dark text on it). */
        fun laneFill(lane: RequestLane): Color = when (lane) {
            RequestLane.ABSENCES -> Color(0xFFF7DCD6)
            RequestLane.REMOTE_AND_EVENTS -> Color(0xFFD5E8F7)
            RequestLane.OVERTIME -> Color(0xFFF4E3B5)
        }

        private val DeclinedFill = Color(0xFFF0F0F0)
        private val DeclinedText = Color(0xFF555555)
        private val DeclinedBorder = Color(0xFF767676)

        /**
         * Approved: solid, the lane's fill with the theme's base as its edge. Pending: outlined,
         * white inside with a thick edge and text in the theme's base. Rejected (shown on
         * request): grey, struck through. RequestSpanContrastTest checks all nine themes.
         */
        fun of(style: RequestSpanStyle, lane: RequestLane, palette: ThemePalette): RequestSpanColors = when (style) {
            RequestSpanStyle.SOLID -> RequestSpanColors(laneFill(lane), palette.base.base, 1.dp, DarkText)
            RequestSpanStyle.OUTLINED -> RequestSpanColors(Track, palette.base.base, 2.dp, palette.base.base)
            RequestSpanStyle.DECLINED -> RequestSpanColors(DeclinedFill, DeclinedBorder, 1.dp, DeclinedText)
        }
    }
}

@Composable
fun RequestLane.label(): String = stringResource(
    when (this) {
        RequestLane.ABSENCES -> Res.string.timeline_lane_absences
        RequestLane.REMOTE_AND_EVENTS -> Res.string.timeline_lane_remote_events
        RequestLane.OVERTIME -> Res.string.timeline_lane_overtime
    }
)

@Composable
fun RequestType.label(): String = when (this) {
    is RequestType.Leave -> type.label()
    is RequestType.Work -> kind.label()
}

/** "10.03.2026 – 14.03.2026", "10.03.2026" or "10.03.2026, 08:00–12:00". */
@Composable
fun TimelineRequest.datesText(): String = when {
    isHourly -> stringResource(Res.string.timeline_request_hours, formatDay(from), startTime.orEmpty(), endTime.orEmpty())
    from == until -> formatDay(from)
    else -> stringResource(Res.string.timeline_from_until, formatDay(from), formatDay(until))
}

/** The span's short label: its type, and for remote work its place. */
@Composable
private fun TimelineRequest.shortLabel(): String {
    val type = type.label()
    return placeName?.takeIf { it.isNotBlank() }?.let { "$type · $it" } ?: type
}

/** Type, dates or hours, status: the tooltip and (with its lane) what screen readers say. */
@Composable
private fun TimelineRequest.tooltipText(): String = stringResource(Res.string.timeline_request_tooltip, shortLabel(), datesText(), status.label())

@Composable
private fun TimelineRequest.description(): String =
    stringResource(Res.string.timeline_request_description, lane.label(), shortLabel(), datesText(), status.label())

/** "Show rejected (2)" / "Hide rejected", offered only while there are rejected requests. */
@Composable
private fun DeclinedToggle(requests: TimelineRequests, onToggle: () -> Unit) {
    if (requests.declinedCount == 0) return
    TextButton(onClick = onToggle, modifier = Modifier.testTag(TimelineRequestTags.TOGGLE)) {
        Text(
            if (requests.showDeclined) stringResource(Res.string.timeline_hide_rejected)
            else stringResource(Res.string.timeline_show_rejected, requests.declinedCount),
            style = MaterialTheme.typography.labelLarge,
            color = DeepNavy
        )
    }
}

// --- Wide: the request lanes on the timeline's axis -------------------------------------------

private val RequestRowHeight = 34.dp
private val HourMarkWidth = 8.dp

/**
 * SH-224: the request lanes below the timeline's own, on the same axis ([first] at x = 0,
 * [pxPerDay]). Each lane has a row per overlap; approved requests are solid, pending outlined;
 * an hour-based request is a narrow mark on its day. A tap opens the request; nothing is dragged.
 */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
internal fun RequestLaneRows(
    requests: TimelineRequests,
    first: LocalDate,
    last: LocalDate,
    pxPerDay: Float,
    trackWidth: Dp,
    labelWidth: Dp,
    months: List<LocalDate>,
    todayX: Float,
    canAdd: Boolean,
    canEdit: Boolean,
    gridLine: Color,
    todayLine: Color,
    onAdd: (RequestLane) -> Unit,
    onOpen: (String) -> Unit,
    onToggleDeclined: () -> Unit
) {
    fun xOf(day: LocalDate): Float = first.daysUntil(day) * pxPerDay
    HorizontalDivider(color = gridLine)
    Row(Modifier.fillMaxWidth().testTag(TimelineRequestTags.SECTION).padding(start = 12.dp, top = 8.dp, end = 8.dp), verticalAlignment = Alignment.CenterVertically) {
        Text(stringResource(Res.string.timeline_requests_title), style = MaterialTheme.typography.titleSmall, color = DeepNavy, fontWeight = FontWeight.Bold)
        Spacer(Modifier.width(12.dp))
        Text(stringResource(Res.string.timeline_requests_hint), style = MaterialTheme.typography.bodySmall, color = DarkText.copy(alpha = 0.75f), modifier = Modifier.weight(1f))
        DeclinedToggle(requests, onToggleDeclined)
    }
    val density = LocalDensity.current
    val palette = AppAppearance.palette
    val noDrag = stringResource(Res.string.timeline_request_no_drag)
    TimelineRequests.LANES.forEach { lane ->
        val rows = requests.rowsOf(lane)
        val height = RequestRowHeight * rows.size.coerceAtLeast(1) + 10.dp
        HorizontalDivider(color = gridLine)
        Row(Modifier.fillMaxWidth().height(height).testTag(TimelineRequestTags.lane(lane)), verticalAlignment = Alignment.CenterVertically) {
            Text(
                lane.label(),
                style = MaterialTheme.typography.labelLarge,
                color = DeepNavy,
                maxLines = 2,
                overflow = TextOverflow.Ellipsis,
                modifier = Modifier.width(labelWidth - 8.dp).padding(start = 12.dp)
            )
            Spacer(Modifier.width(8.dp))
            Box(
                Modifier.width(trackWidth).fillMaxHeight().drawBehind {
                    months.forEach { m -> drawLine(gridLine, Offset(xOf(m), 0f), Offset(xOf(m), size.height), 1f) }
                    drawLine(todayLine, Offset(todayX, 0f), Offset(todayX, size.height), 2.dp.toPx())
                }
            ) {
                val trackPx = with(density) { trackWidth.toPx() }
                rows.forEachIndexed { row, list ->
                    list.forEachIndexed { index, request ->
                        val start = maxOf(request.from, first)
                        val end = minOf(request.until, last)
                        if (end < start) return@forEachIndexed
                        val x = xOf(start)
                        val spanPx = if (request.isHourly) with(density) { HourMarkWidth.toPx() }
                        else ((start.daysUntil(end) + 1) * pxPerDay).coerceAtLeast(with(density) { 12.dp.toPx() })
                        // A span too narrow for its label has it beside it, up to the next request of its row.
                        val nextX = list.getOrNull(index + 1)?.let { xOf(maxOf(it.from, first)) } ?: trackPx
                        val besidePx = (nextX - x - spanPx - with(density) { 8.dp.toPx() }).coerceAtLeast(0f)
                        val inside = !request.isHourly && spanPx >= with(density) { 96.dp.toPx() }
                        val colors = RequestSpanColors.of(request.style, lane, palette)
                        val tip = request.tooltipText().let { if (canEdit) "$it\n$noDrag" else it }
                        val description = request.description()
                        val label = if (request.isHourly) "${request.startTime}–${request.endTime} ${request.type.label()}" else request.shortLabel()
                        TooltipBox(
                            positionProvider = TooltipDefaults.rememberTooltipPositionProvider(TooltipAnchorPosition.Above),
                            tooltip = { PlainTooltip { Text(tip) } },
                            state = rememberTooltipState(),
                            modifier = Modifier.offset { IntOffset(x.roundToInt(), with(density) { (5.dp + RequestRowHeight * row).roundToPx() }) }
                        ) {
                            Row(
                                Modifier.height(RequestRowHeight - 6.dp).testTag(TimelineRequestTags.request(request.key))
                                    .semantics(mergeDescendants = true) { contentDescription = description }
                                    .clickable { onOpen(request.key) },
                                verticalAlignment = Alignment.CenterVertically
                            ) {
                                // Approved: solid; pending: outlined; part of a day: a narrow mark on its day.
                                val shape = RoundedCornerShape(if (request.isHourly) 3.dp else 8.dp)
                                Box(
                                    Modifier.width(with(density) { spanPx.toDp() }).fillMaxHeight().clip(shape).background(colors.fill)
                                        .border(colors.borderWidth, colors.border, shape)
                                        .padding(horizontal = if (inside) 8.dp else 0.dp),
                                    contentAlignment = Alignment.CenterStart
                                ) {
                                    if (inside) SpanText(label, colors.text, request.style)
                                }
                                if (!inside && besidePx >= with(density) { 24.dp.toPx() }) {
                                    Spacer(Modifier.width(4.dp))
                                    Box(Modifier.width(with(density) { minOf(besidePx, 180.dp.toPx()).toDp() })) {
                                        SpanText(label, DarkText, request.style)
                                    }
                                }
                            }
                        }
                    }
                }
            }
            if (canAdd) {
                IconButtonWithTooltip(onClick = { onAdd(lane) }, modifier = Modifier.size(40.dp), tooltip = stringResource(Res.string.timeline_add_to, lane.label())) {
                    Icon(Icons.Default.Add, contentDescription = stringResource(Res.string.timeline_add_to, lane.label()), tint = DeepNavy)
                }
            }
        }
    }
}

@Composable
private fun SpanText(text: String, color: Color, style: RequestSpanStyle) {
    Text(
        text,
        style = MaterialTheme.typography.labelMedium,
        color = color,
        fontWeight = if (style == RequestSpanStyle.OUTLINED) FontWeight.SemiBold else FontWeight.Medium,
        textDecoration = if (style == RequestSpanStyle.DECLINED) TextDecoration.LineThrough else null,
        maxLines = 1,
        overflow = TextOverflow.Ellipsis
    )
}

// --- Phones: a list per request lane ----------------------------------------------------------

/** SH-224: the request lanes as lists, newest first, each request with its style dot, dates and status. */
@Composable
internal fun RequestLaneLists(
    requests: TimelineRequests,
    canAdd: Boolean,
    onAdd: (RequestLane) -> Unit,
    onOpen: (String) -> Unit,
    onToggleDeclined: () -> Unit
) {
    val palette = AppAppearance.palette
    Row(Modifier.fillMaxWidth().testTag(TimelineRequestTags.SECTION), verticalAlignment = Alignment.CenterVertically) {
        Column(Modifier.weight(1f)) {
            Text(stringResource(Res.string.timeline_requests_title), style = MaterialTheme.typography.titleMedium, color = DeepNavy, fontWeight = FontWeight.Bold)
            Text(stringResource(Res.string.timeline_requests_hint), style = MaterialTheme.typography.bodySmall, color = DarkText.copy(alpha = 0.75f))
        }
        DeclinedToggle(requests, onToggleDeclined)
    }
    TimelineRequests.LANES.forEach { lane ->
        Card(
            colors = CardDefaults.cardColors(containerColor = RequestSpanColors.Track),
            modifier = Modifier.fillMaxWidth().testTag(TimelineRequestTags.lane(lane))
        ) {
            Column(Modifier.padding(horizontal = 16.dp, vertical = 8.dp)) {
                Row(verticalAlignment = Alignment.CenterVertically) {
                    Text(lane.label(), style = MaterialTheme.typography.titleSmall, color = DeepNavy, fontWeight = FontWeight.Bold, modifier = Modifier.weight(1f))
                    if (canAdd) {
                        IconButtonWithTooltip(onClick = { onAdd(lane) }, tooltip = stringResource(Res.string.timeline_add_to, lane.label())) {
                            Icon(Icons.Default.Add, contentDescription = stringResource(Res.string.timeline_add_to, lane.label()), tint = DeepNavy)
                        }
                    }
                }
                val list = requests.of(lane)
                if (list.isEmpty()) {
                    Text(stringResource(Res.string.timeline_empty_lane), style = MaterialTheme.typography.bodySmall, color = DarkText.copy(alpha = 0.7f))
                }
                list.reversed().forEach { request ->
                    val colors = RequestSpanColors.of(request.style, lane, palette)
                    val description = request.description()
                    Row(
                        Modifier.fillMaxWidth()
                            .testTag(TimelineRequestTags.request(request.key))
                            .semantics(mergeDescendants = true) { contentDescription = description }
                            .clickable { onOpen(request.key) }
                            .padding(vertical = 8.dp)
                            .let { if (request.style == RequestSpanStyle.DECLINED) it.alpha(0.85f) else it },
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        // Solid: filled; pending: an outlined ring.
                        Box(Modifier.size(14.dp).clip(CircleShape).background(colors.fill).border(colors.borderWidth, colors.border, CircleShape))
                        Spacer(Modifier.width(12.dp))
                        Column(Modifier.weight(1f)) {
                            Text(
                                request.shortLabel(), style = MaterialTheme.typography.bodyMedium, color = DarkText, fontWeight = FontWeight.Medium,
                                textDecoration = if (request.style == RequestSpanStyle.DECLINED) TextDecoration.LineThrough else null
                            )
                            Text(request.datesText(), style = MaterialTheme.typography.bodySmall, color = DarkText.copy(alpha = 0.75f))
                        }
                        Text(
                            request.status.label(),
                            style = MaterialTheme.typography.labelSmall,
                            color = colors.text,
                            fontWeight = FontWeight.Bold,
                            modifier = Modifier.clip(RoundedCornerShape(8.dp)).background(colors.fill)
                                .border(colors.borderWidth, colors.border, RoundedCornerShape(8.dp)).padding(horizontal = 8.dp, vertical = 4.dp)
                        )
                    }
                }
            }
        }
    }
}

// --- The request's detail ------------------------------------------------------------------------

/**
 * SH-224: a request opened from the timeline: its type, dates or hours, status, place and notes.
 * A manager who decides requests approves or rejects a pending one and withdraws any (SH-223's
 * rules); everyone else reads it. It says requests are not dragged.
 */
@Composable
internal fun TimelineRequestDialog(
    request: TimelineRequest,
    canDecide: Boolean,
    canEdit: Boolean,
    saving: Boolean,
    onApprove: () -> Unit,
    onReject: () -> Unit,
    onWithdraw: () -> Unit,
    onDismiss: () -> Unit
) {
    val pending = request.status == AbsenceStatus.PENDING
    EvoDialog(
        title = request.type.label(),
        subtitle = request.lane.label(),
        icon = Icons.Default.EventNote,
        onDismiss = onDismiss,
        modifier = Modifier.testTag(TimelineRequestTags.DETAIL),
        confirm = if (canDecide && pending) DialogAction(stringResource(Res.string.absences_action_approve), onApprove, loading = saving) else null,
        neutral = when {
            canDecide && pending -> DialogAction(stringResource(Res.string.absences_action_reject), onReject, destructive = true)
            canDecide -> DialogAction(stringResource(Res.string.timeline_request_withdraw), onWithdraw, destructive = true)
            else -> null
        },
        dismiss = DialogAction(stringResource(Res.string.timeline_request_close), onDismiss)
    ) {
        val on = com.evoapps.evoshift.widgets.LocalOnColours.current
        Text(request.datesText(), style = MaterialTheme.typography.titleMedium, color = on.onSurface, fontWeight = FontWeight.SemiBold)
        Text(stringResource(Res.string.timeline_request_status, request.status.label()), style = MaterialTheme.typography.bodyLarge, color = on.onSurface)
        request.placeName?.takeIf { it.isNotBlank() }?.let {
            Text(stringResource(Res.string.timeline_request_place, it), style = MaterialTheme.typography.bodyMedium, color = on.onSurface)
        }
        request.absence?.let { AbsenceDetailsLines(it, on.onSurfaceVariant) }
        request.note?.takeIf { it.isNotBlank() }?.let {
            Text(stringResource(Res.string.timeline_request_note, it), style = MaterialTheme.typography.bodyMedium, color = on.onSurfaceVariant)
        }
        request.rejectionReason?.takeIf { request.status == AbsenceStatus.REJECTED && it.isNotBlank() }?.let {
            Text(stringResource(Res.string.absences_rejection_reason, it), style = MaterialTheme.typography.bodyMedium, color = on.onSurfaceVariant)
        }
        if (canEdit) Text(stringResource(Res.string.timeline_request_no_drag), style = MaterialTheme.typography.bodySmall, color = on.onSurfaceVariant)
        // A pending request is withdrawn here too (the dialog's buttons approve and reject it).
        if (canDecide && pending) {
            TextButton(onClick = onWithdraw) {
                Text(stringResource(Res.string.timeline_request_withdraw), color = on.error, fontWeight = FontWeight.SemiBold)
            }
        }
    }
}
